/**
 * STATUTORY & DEDUCTIONS.
 *
 * The last placeholder on the Service side, replaced. Six questions Accounts
 * currently answers by hand out of the RA bill register, and every figure on
 * this screen is already stored on an approved bill — nothing here computes
 * tax, it only groups what fn_ra_recalc wrote and froze.
 *
 * THE ONE DISTINCTION THIS SCREEN HAS TO MAKE, AND MAKES LOUDLY
 * ------------------------------------------------------------
 * Three of the six are FLOWS and three are POSITIONS, and mixing them is how a
 * quarter's TDS gets compared against a retention balance that has been
 * building since March.
 *
 *   FOR THE PERIOD    reverse-charge GST, TDS, input credit. What happened
 *                     between two dates. Changes when the period changes.
 *   AS AT TODAY       retention held, advances outstanding, certified not
 *                     billed. A balance. The period selector does not touch
 *                     them, and the heading says so.
 *
 * So they are in two groups under two headings, and the period control sits
 * inside the first group rather than at the top of the page, where it would
 * look like it governed both.
 *
 * WHY THE PERIOD IS A FINANCIAL YEAR AND A QUARTER
 * ------------------------------------------------
 * Because the TDS return is quarterly and the financial year runs April to
 * March. One control in the shape of the thing being filed beats two controls
 * in the shape of a calendar. Reverse-charge GST is monthly, so the RCM section
 * breaks the chosen period down by month underneath its total — both shapes out
 * of one selector, and nobody has to know which section wants which.
 *
 * WHICH DATE PUTS A BILL IN A PERIOD
 * ----------------------------------
 * Its APPROVAL date, in IST. TDS is deducted at credit or payment, whichever is
 * earlier, and in OVAL IMS the credit is the approved bill — that is when the
 * payable is raised and the contractor becomes a creditor. Decided 17-09-2026.
 * The reasoning is in migration 0139 because that is where it is implemented;
 * this screen only says which date it is, on the face of it, so nobody has to
 * go looking.
 *
 * EVERY BILL IS READ ONCE AND FILTERED HERE
 * -----------------------------------------
 * v_statutory_bills is one row per approved bill, and OVAL will write a few
 * hundred a year. Reading them all once makes changing the period instant and
 * keeps the financial-year list honest — it is built from the bills that exist
 * rather than from a guess. If this ever passes a few thousand rows, move the
 * financial year onto the query as .eq('fy_start', fy); nothing else changes.
 */
import { useMemo, useState } from 'react'
import {
  AlertTriangle, Banknote, FileSpreadsheet, HandCoins, Landmark, Percent,
  PiggyBank, Receipt, ShieldAlert,
} from 'lucide-react'
import { useQuery } from '@/lib/data'
import { downloadFromFunction, supabase } from '@/lib/supabase'
import { ddmmyyyy, rupees } from '@/lib/format'
import { Card, EmptyState, Loading, Select, Stat, useToast } from '@/components/ui'

const n = (v: unknown) => Number(v ?? 0) || 0

interface StatBill {
  ra_id: string; wo_id: string; ra_no: number
  site_id: string; site_name: string | null
  vendor_id: string; vendor_name: string | null
  vendor_pan: string | null; vendor_gstin: string | null
  is_gst_registered: boolean
  bill_date: string; approval_date: string; reckon_date: string
  fy_start: number; fy_label: string; quarter_no: number
  month_key: string; month_label: string
  gross_value: number; gst_treatment: string; gst_rate_percent: number
  gst_input_credit: number
  is_reverse_charge: boolean; rcm_gst_amount: number
  tds_pct: number | null; tds_section: string; tds_amount: number
  retention_amount: number; advance_recovered: number; net_payable: number
}

interface RetentionRow {
  wo_id: string; site_name: string | null
  vendor_name: string | null; vendor_pan: string | null
  title: string | null; wo_status: string
  retention_pct: number; completion_date: string | null
  defect_liability_months: number | null; dlp_ends_on: string | null
  retention_held: number; first_tranche: number; second_tranche: number
  work_certified_complete: boolean; dlp_expired: boolean
  days_to_dlp_end: number | null
}

interface AdvanceRow {
  wo_id: string; site_name: string | null; vendor_name: string | null
  title: string | null; wo_status: string
  advance_pct: number; advance_entitled: number
  advance_recovered: number; advance_outstanding: number
}

interface BillingRow {
  wo_id: string; site_name: string | null; vendor_name: string | null
  title: string | null; contract_value: number
  value_certified: number; value_billed: number
  bills_approved: number; last_ra_no: number | null
}

const QUARTERS = [
  { value: '0', label: 'Whole year' },
  { value: '1', label: 'Q1 — Apr to Jun' },
  { value: '2', label: 'Q2 — Jul to Sep' },
  { value: '3', label: 'Q3 — Oct to Dec' },
  { value: '4', label: 'Q4 — Jan to Mar' },
]

/** The financial year a date falls in. April to March. */
function fyOf(d: Date): number {
  return d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1
}
const fyLabel = (start: number) => `${start}-${String((start + 1) % 100).padStart(2, '0')}`

export function Statutory() {
  const toast = useToast()
  const [fy, setFy] = useState<number>(fyOf(new Date()))
  const [quarter, setQuarter] = useState('0')
  const [busy, setBusy] = useState(false)

  const { data: bills, loading } = useQuery<StatBill>(
    'stat_bills',
    () => supabase.from('v_statutory_bills').select('*').order('reckon_date'),
    [], { cacheable: false, idField: 'ra_id' })

  const { data: retention } = useQuery<RetentionRow>(
    'stat_retention',
    () => supabase.from('v_statutory_retention').select('*').order('dlp_ends_on'),
    [], { cacheable: false, idField: 'wo_id' })

  const { data: advances } = useQuery<AdvanceRow>(
    'stat_advance',
    () => supabase.from('v_statutory_advance').select('*'),
    [], { cacheable: false, idField: 'wo_id' })

  const { data: billing } = useQuery<BillingRow>(
    'stat_billing',
    () => supabase.from('v_wo_billing_position')
      .select('wo_id, site_name, vendor_name, title, contract_value, value_certified, value_billed, bills_approved, last_ra_no'),
    [], { cacheable: false, idField: 'wo_id' })

  // The financial years that actually have bills, newest first, with the
  // current one always present so a fresh year is selectable before its first
  // bill is approved.
  const years = useMemo(() => {
    const s = new Set<number>(bills.map((b) => b.fy_start))
    s.add(fyOf(new Date()))
    return [...s].sort((a, b) => b - a)
  }, [bills])

  const inPeriod = useMemo(() => bills.filter((b) =>
    b.fy_start === fy && (quarter === '0' || String(b.quarter_no) === quarter)
  ), [bills, fy, quarter])

  const periodLabel = quarter === '0'
    ? `FY ${fyLabel(fy)}`
    : `${QUARTERS.find((q) => q.value === quarter)?.label.split(' — ')[0]} of FY ${fyLabel(fy)}`

  // ---- the three flows -----------------------------------------------------
  const rcmTotal = inPeriod.reduce((a, b) => a + n(b.rcm_gst_amount), 0)
  const tdsTotal = inPeriod.reduce((a, b) => a + n(b.tds_amount), 0)
  const itcTotal = inPeriod.reduce((a, b) => a + n(b.gst_input_credit), 0)

  /** Reverse charge, by month and then by contractor inside it. */
  const rcmByMonth = useMemo(() => {
    const m = new Map<string, { label: string; taxable: number; gst: number; bills: number }>()
    for (const b of inPeriod) {
      if (!n(b.rcm_gst_amount)) continue
      const e = m.get(b.month_key) ?? { label: b.month_label, taxable: 0, gst: 0, bills: 0 }
      e.taxable += n(b.gross_value); e.gst += n(b.rcm_gst_amount); e.bills += 1
      m.set(b.month_key, e)
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [inPeriod])

  /** TDS, by section then contractor — the shape the return is filed in. */
  const tdsRows = useMemo(() => {
    const m = new Map<string, {
      section: string; vendor: string; pan: string | null; pct: number | null
      credited: number; tds: number; bills: number
    }>()
    for (const b of inPeriod) {
      if (!n(b.tds_amount)) continue
      const key = `${b.tds_section}|${b.vendor_id}|${b.tds_pct ?? ''}`
      const e = m.get(key) ?? {
        section: b.tds_section, vendor: b.vendor_name ?? b.vendor_id,
        pan: b.vendor_pan, pct: b.tds_pct, credited: 0, tds: 0, bills: 0,
      }
      e.credited += n(b.gross_value); e.tds += n(b.tds_amount); e.bills += 1
      m.set(key, e)
    }
    return [...m.values()].sort((a, b) =>
      a.section.localeCompare(b.section) || b.tds - a.tds)
  }, [inPeriod])

  /** Input credit, by month. Only registered contractors ever appear. */
  const itcByMonth = useMemo(() => {
    const m = new Map<string, { label: string; taxable: number; gst: number; bills: number }>()
    for (const b of inPeriod) {
      if (!n(b.gst_input_credit)) continue
      const e = m.get(b.month_key) ?? { label: b.month_label, taxable: 0, gst: 0, bills: 0 }
      e.taxable += n(b.gross_value); e.gst += n(b.gst_input_credit); e.bills += 1
      m.set(b.month_key, e)
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [inPeriod])

  // ---- the three positions -------------------------------------------------
  const retHeld = retention.reduce((a, r) => a + n(r.retention_held), 0)
  const retDue = retention.reduce((a, r) =>
    a + (r.work_certified_complete ? n(r.first_tranche) : 0)
      + (r.dlp_expired && r.work_certified_complete ? n(r.second_tranche) : 0), 0)
  const advOut = advances.reduce((a, r) => a + n(r.advance_outstanding), 0)
  const accrual = billing.reduce((a, r) =>
    a + Math.max(n(r.value_certified) - n(r.value_billed), 0), 0)
  const accrualRows = billing
    .filter((r) => n(r.value_certified) - n(r.value_billed) > 0.005)
    .sort((a, b) => (n(b.value_certified) - n(b.value_billed))
                  - (n(a.value_certified) - n(a.value_billed)))

  async function download() {
    setBusy(true)
    try {
      await downloadFromFunction(
        'statutory-pack',
        { fy_start: fy, quarter: Number(quarter) },
        `OVAL-Statutory-${fyLabel(fy)}${quarter === '0' ? '' : `-Q${quarter}`}.xlsx`)
      toast.success('The statutory pack has been downloaded.')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'The pack could not be built')
    } finally { setBusy(false) }
  }

  if (loading) return <Loading label="Reading the bills…" />

  const Empty = ({ what }: { what: string }) => (
    <p className="py-5 text-center text-sm text-slate-500">{what}</p>
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Statutory &amp; Deductions</h1>
          <p className="text-sm text-slate-600">
            Everything deducted, withheld or owed on the works contracts. Taken from
            approved running account bills only.
          </p>
        </div>
        <button className="btn-primary btn-sm" disabled={busy} onClick={() => void download()}>
          <FileSpreadsheet className="h-4 w-4" />
          {busy ? 'Building…' : 'Download the pack'}
        </button>
      </div>

      {/* =================================================================== */}
      {/* FOR THE PERIOD                                                      */}
      {/* =================================================================== */}
      <Card
        title="For the period"
        subtitle="A bill falls in the period its APPROVAL cleared, in IST — the date the payable was raised, which is the credit TDS is deducted against."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select className="w-auto" value={String(fy)}
                    onChange={(e) => setFy(Number(e.target.value))}
                    options={years.map((y) => ({ value: String(y), label: `FY ${fyLabel(y)}` }))} />
            <Select className="w-auto" value={quarter}
                    onChange={(e) => setQuarter(e.target.value)}
                    options={QUARTERS} />
          </div>
        }
      >
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label="Reverse-charge GST payable" value={rupees(rcmTotal)}
                sub="OVAL owes this to the government, not to the contractor"
                tone={rcmTotal > 0 ? 'warn' : 'default'}
                icon={<ShieldAlert className="h-4 w-4" />} />
          <Stat label="TDS deducted" value={rupees(tdsTotal)}
                sub={`${periodLabel} — to be deposited and returned`}
                icon={<Percent className="h-4 w-4" />} />
          <Stat label="Input credit available" value={rupees(itcTotal)}
                sub="GST charged by registered contractors"
                icon={<Receipt className="h-4 w-4" />} />
        </div>

        {inPeriod.length === 0 && (
          <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            No bill was approved in {periodLabel}. The figures above are zero because
            nothing fell in the period, not because nothing was deducted.
          </p>
        )}
      </Card>

      {/* ---------- reverse charge ---------- */}
      <Card title="Reverse-charge GST, by month"
            subtitle="Unregistered contractors. He charges nothing; OVAL pays the government directly and none of it reaches him.">
        {rcmByMonth.length === 0 ? <Empty what="No reverse-charge liability arose in this period." /> : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Month</th><th className="num">Bills</th>
                  <th className="num">Taxable value</th><th className="num">GST payable</th>
                </tr>
              </thead>
              <tbody>
                {rcmByMonth.map(([key, m]) => (
                  <tr key={key}>
                    <td className="whitespace-nowrap font-medium">{m.label}</td>
                    <td className="num">{m.bills}</td>
                    <td className="num">{rupees(m.taxable)}</td>
                    <td className="num font-semibold text-amber-700">{rupees(m.gst)}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-semibold">
                  <td>Total</td>
                  <td className="num">{rcmByMonth.reduce((a, [, m]) => a + m.bills, 0)}</td>
                  <td className="num">{rupees(rcmByMonth.reduce((a, [, m]) => a + m.taxable, 0))}</td>
                  <td className="num text-amber-700">{rupees(rcmTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ---------- TDS ---------- */}
      <Card title="Tax deducted at source"
            subtitle="By section and contractor, which is the shape the quarterly return is filed in.">
        {tdsRows.length === 0 ? <Empty what="No TDS was deducted in this period." /> : (
          <>
            <div className="overflow-x-auto">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Section</th><th>Contractor</th><th>PAN</th>
                    <th className="num">Rate</th><th className="num">Bills</th>
                    <th className="num">Amount credited</th><th className="num">TDS</th>
                  </tr>
                </thead>
                <tbody>
                  {tdsRows.map((r, i) => (
                    <tr key={i}>
                      <td className="whitespace-nowrap font-medium">{r.section}</td>
                      <td className="max-w-[220px] truncate">{r.vendor}</td>
                      <td className="whitespace-nowrap font-mono text-xs">
                        {r.pan ?? <span className="text-red-600">Not on record</span>}
                      </td>
                      <td className="num">{r.pct == null ? '—' : `${n(r.pct)}%`}</td>
                      <td className="num">{r.bills}</td>
                      <td className="num">{rupees(r.credited)}</td>
                      <td className="num font-semibold">{rupees(r.tds)}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50 font-semibold">
                    <td colSpan={5}>Total</td>
                    <td className="num">{rupees(tdsRows.reduce((a, r) => a + r.credited, 0))}</td>
                    <td className="num">{rupees(tdsTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* Section 194C of the 1961 Act became 393(1) of the Income-tax Act
                2025 from 01-04-2026. Both may appear at once on an order raised
                before that date, so the table groups by section and never adds
                the two together. */}
            <p className="mt-2 text-xs text-slate-500">
              Section 393(1) of the Income-tax Act 2025 replaced section 194C of the 1961
              Act for everything paid on or after 01-04-2026. An order raised earlier still
              carries 194C and is reported separately.
            </p>
            {tdsRows.some((r) => !r.pan) && (
              <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-800">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                A contractor above has no PAN on record. TDS without a PAN is deductible at
                20% and the return will not validate — fix it on the vendor master before filing.
              </p>
            )}
          </>
        )}
      </Card>

      {/* ---------- input credit ---------- */}
      <Card title="Input credit, by month"
            subtitle="GST charged by registered contractors on approved bills. Claimable against OVAL's own output tax.">
        {itcByMonth.length === 0 ? <Empty what="No registered contractor billed GST in this period." /> : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Month</th><th className="num">Bills</th>
                  <th className="num">Taxable value</th><th className="num">Input credit</th>
                </tr>
              </thead>
              <tbody>
                {itcByMonth.map(([key, m]) => (
                  <tr key={key}>
                    <td className="whitespace-nowrap font-medium">{m.label}</td>
                    <td className="num">{m.bills}</td>
                    <td className="num">{rupees(m.taxable)}</td>
                    <td className="num font-semibold text-emerald-700">{rupees(m.gst)}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-semibold">
                  <td>Total</td>
                  <td className="num">{itcByMonth.reduce((a, [, m]) => a + m.bills, 0)}</td>
                  <td className="num">{rupees(itcByMonth.reduce((a, [, m]) => a + m.taxable, 0))}</td>
                  <td className="num text-emerald-700">{rupees(itcTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* =================================================================== */}
      {/* AS AT TODAY                                                         */}
      {/* =================================================================== */}
      <Card title="As at today"
            subtitle="Balances, not flows. The period above does not change these — they are what OVAL is holding and owed right now.">
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label="Retention held" value={rupees(retHeld)}
                sub={retDue > 0 ? `${rupees(retDue)} has fallen due for release` : 'None yet due for release'}
                tone={retDue > 0 ? 'warn' : 'default'}
                icon={<PiggyBank className="h-4 w-4" />} />
          <Stat label="Advances outstanding" value={rupees(advOut)}
                sub="Still to be recovered from future bills"
                icon={<HandCoins className="h-4 w-4" />} />
          <Stat label="Certified, not yet billed" value={rupees(accrual)}
                sub="The month-end accrual, at contract rates"
                icon={<Banknote className="h-4 w-4" />} />
        </div>
      </Card>

      {/* ---------- retention ---------- */}
      <Card title="Retention held, and what is due back"
            subtitle="Clause 150: half on the work being completed and certified, the balance on expiry of the Defect Liability Period.">
        {retention.length === 0 ? <Empty what="No retention has been deducted on any contract." /> : (
          <>
            <div className="overflow-x-auto">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Work order</th><th>Contractor</th>
                    <th className="num">Held</th>
                    <th className="num">1st half — on completion</th>
                    <th className="num">2nd half — on DLP expiry</th>
                    <th>DLP ends</th>
                  </tr>
                </thead>
                <tbody>
                  {retention.map((r) => {
                    const firstDue = r.work_certified_complete
                    const secondDue = r.work_certified_complete && r.dlp_expired
                    return (
                      <tr key={r.wo_id}>
                        <td className="whitespace-nowrap">
                          <span className="font-medium">{r.wo_id}</span>
                          <span className="block max-w-[200px] truncate text-[11px] text-slate-400">
                            {r.site_name} · {n(r.retention_pct)}%
                          </span>
                        </td>
                        <td className="max-w-[180px] truncate">{r.vendor_name}</td>
                        <td className="num font-semibold">{rupees(r.retention_held)}</td>
                        <td className="num">
                          {rupees(r.first_tranche)}
                          <span className={firstDue
                            ? 'block text-[11px] font-semibold text-amber-700'
                            : 'block text-[11px] text-slate-400'}>
                            {firstDue ? 'DUE' : 'work not certified complete'}
                          </span>
                        </td>
                        <td className="num">
                          {rupees(r.second_tranche)}
                          <span className={secondDue
                            ? 'block text-[11px] font-semibold text-amber-700'
                            : 'block text-[11px] text-slate-400'}>
                            {secondDue ? 'DUE'
                              : r.dlp_expired ? 'DLP over, work not certified'
                              : r.days_to_dlp_end == null ? 'no completion date'
                              : `${r.days_to_dlp_end} days to go`}
                          </span>
                        </td>
                        <td className="whitespace-nowrap text-xs">
                          {r.dlp_ends_on ? ddmmyyyy(r.dlp_ends_on) : '—'}
                          <span className="block text-[11px] text-slate-400">
                            {r.defect_liability_months ?? 0} months
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {/* An honest statement of a known gap rather than a silent wrong
                answer. There is no rpc_wo_close yet, so "completed and
                certified" can only be read off the order status. */}
            <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
              &quot;Completed and certified&quot; is read from the work order reaching FULFILLED
              or CLOSED. Until order closure is built, an order that is finished on site but
              still open here will show its first half as not yet due.
            </p>
          </>
        )}
      </Card>

      {/* ---------- advances ---------- */}
      <Card title="Advances outstanding"
            subtitle="Advance due under the order, less what has been recovered from bills.">
        {advances.filter((a) => n(a.advance_outstanding) > 0.005).length === 0
          ? <Empty what="Every advance has been fully recovered." /> : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Work order</th><th>Contractor</th>
                  <th className="num">Advance</th><th className="num">Recovered</th>
                  <th className="num">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {advances.filter((a) => n(a.advance_outstanding) > 0.005)
                  .sort((a, b) => n(b.advance_outstanding) - n(a.advance_outstanding))
                  .map((r) => (
                    <tr key={r.wo_id}>
                      <td className="whitespace-nowrap">
                        <span className="font-medium">{r.wo_id}</span>
                        <span className="block max-w-[200px] truncate text-[11px] text-slate-400">
                          {r.site_name} · {n(r.advance_pct)}% of order
                        </span>
                      </td>
                      <td className="max-w-[180px] truncate">{r.vendor_name}</td>
                      <td className="num">{rupees(r.advance_entitled)}</td>
                      <td className="num text-emerald-700">{rupees(r.advance_recovered)}</td>
                      <td className="num font-semibold">{rupees(r.advance_outstanding)}</td>
                    </tr>
                  ))}
                <tr className="bg-slate-50 font-semibold">
                  <td colSpan={4}>Total outstanding</td>
                  <td className="num">{rupees(advOut)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ---------- accrual ---------- */}
      <Card title="Certified but not yet billed"
            subtitle="Work the Measurement Book has certified that no running account bill covers. The accrual at month end, and the prompt to raise the next bill.">
        {accrualRows.length === 0
          ? <Empty what="Everything certified has been billed." /> : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Work order</th><th>Contractor</th>
                  <th className="num">Certified</th><th className="num">Billed</th>
                  <th className="num">Accrual</th><th className="num">Bills so far</th>
                </tr>
              </thead>
              <tbody>
                {accrualRows.map((r) => (
                  <tr key={r.wo_id}>
                    <td className="whitespace-nowrap">
                      <span className="font-medium">{r.wo_id}</span>
                      <span className="block max-w-[200px] truncate text-[11px] text-slate-400">
                        {r.site_name}
                      </span>
                    </td>
                    <td className="max-w-[180px] truncate">{r.vendor_name}</td>
                    <td className="num">{rupees(r.value_certified)}</td>
                    <td className="num">{rupees(r.value_billed)}</td>
                    <td className="num font-semibold text-amber-700">
                      {rupees(n(r.value_certified) - n(r.value_billed))}
                    </td>
                    <td className="num text-xs">{r.bills_approved}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-semibold">
                  <td colSpan={4}>Total accrual</td>
                  <td className="num text-amber-700">{rupees(accrual)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {bills.length === 0 && retention.length === 0 && advances.length === 0 && (
        <EmptyState icon={<Landmark className="h-8 w-8" />}
                    title="Nothing to report yet"
                    message="The pack fills in as running account bills are approved. Nothing here is typed — every figure comes off a bill that has cleared its chain." />
      )}
    </div>
  )
}

export default Statutory
